
import React, { createContext, useContext, useReducer, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useLocation } from 'react-router-dom';
import { supabase, setSupabaseJWT } from '@/integrations/supabase/client';
// Removed toast import to prevent hook call errors

export interface FieldState {
  value: any;
  timestamp: string;
  metadata?: Record<string, any>;
  isDirty: boolean;
}

export interface PageStateData {
  fields: Map<string, FieldState>;
  route: string;
  lastModified: string;
  version: string;
}

interface PageStateContext {
  trackedFields: Map<string, FieldState>;
  updateField: (fieldId: string, value: any, metadata?: any) => void;
  getFieldValue: (fieldId: string) => any;
  saveState: () => Promise<void>;
  loadState: () => Promise<void>;
  clearState: () => void;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
}

type PageStateAction = 
  | { type: 'UPDATE_FIELD'; fieldId: string; value: any; metadata?: any }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_SAVING'; saving: boolean }
  | { type: 'SET_LAST_SAVED'; timestamp: Date | null }
  | { type: 'LOAD_STATE'; fields: Map<string, FieldState> }
  | { type: 'CLEAR_STATE' };

interface PageStateReducerState {
  trackedFields: Map<string, FieldState>;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
}

const initialState: PageStateReducerState = {
  trackedFields: new Map(),
  isLoading: false,
  isSaving: false,
  lastSaved: null,
  hasUnsavedChanges: false,
};

function pageStateReducer(state: PageStateReducerState, action: PageStateAction): PageStateReducerState {
  switch (action.type) {
    case 'UPDATE_FIELD':
      const newFields = new Map(state.trackedFields);
      newFields.set(action.fieldId, {
        value: action.value,
        timestamp: new Date().toISOString(),
        metadata: action.metadata,
        isDirty: true,
      });
      return {
        ...state,
        trackedFields: newFields,
        hasUnsavedChanges: true,
      };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.loading };
    
    case 'SET_SAVING':
      return { ...state, isSaving: action.saving };
    
    case 'SET_LAST_SAVED':
      return { 
        ...state, 
        lastSaved: action.timestamp,
        hasUnsavedChanges: false,
      };
    
    case 'LOAD_STATE':
      return {
        ...state,
        trackedFields: action.fields,
        hasUnsavedChanges: false,
      };
    
    case 'CLEAR_STATE':
      return {
        ...state,
        trackedFields: new Map(),
        hasUnsavedChanges: false,
      };
    
    default:
      return state;
  }
}

const PageStateContext = createContext<PageStateContext | undefined>(undefined);

export const PageStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(pageStateReducer, initialState);
  const { user, isAuthenticated, getToken } = useAuth();
  const location = useLocation();
  const currentRoute = location.pathname;

  const updateField = useCallback((fieldId: string, value: any, metadata?: any) => {
    dispatch({ type: 'UPDATE_FIELD', fieldId, value, metadata });
  }, []);

  const getFieldValue = useCallback((fieldId: string) => {
    return state.trackedFields.get(fieldId)?.value;
  }, [state.trackedFields]);

  const saveState = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      console.warn('Cannot save state: user not authenticated');
      return;
    }

    dispatch({ type: 'SET_SAVING', saving: true });

    try {
      // Get JWT token for authentication
      const token = await getToken();
      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      // Set the JWT token for Supabase to use in RLS
      setSupabaseJWT(token);

      // Convert Map to plain object for JSON serialization
      const fieldsObject = Object.fromEntries(
        Array.from(state.trackedFields.entries()).map(([key, value]) => [
          key,
          {
            value: value.value,
            timestamp: value.timestamp,
            metadata: value.metadata,
            isDirty: value.isDirty,
          }
        ])
      );

      const stateData = {
        fields: fieldsObject,
        route: currentRoute,
        lastModified: new Date().toISOString(),
        version: '1.0.0',
      };

      // Use RPC call with JWT token in headers
      const { error } = await supabase
        .from('user_page_states')
        .upsert({
          user_id: user.id,
          route: currentRoute,
          state_data: stateData,
          last_modified: new Date().toISOString(),
          version: '1.0.0',
        }, {
          onConflict: 'user_id,route'
        });

      if (error) {
        throw error;
      }

      dispatch({ type: 'SET_LAST_SAVED', timestamp: new Date() });
      console.log('Page state saved successfully');
    } catch (error) {
      console.error('Failed to save page state:', error);
      // Don't use toast here as it can cause hook call errors
    } finally {
      dispatch({ type: 'SET_SAVING', saving: false });
    }
  }, [isAuthenticated, user?.id, state.trackedFields, currentRoute, getToken]);

  const loadState = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    dispatch({ type: 'SET_LOADING', loading: true });

    try {
      // Get JWT token for authentication
      const token = await getToken();
      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      // Set the JWT token for Supabase to use in RLS
      setSupabaseJWT(token);

      const { data, error } = await supabase
        .from('user_page_states')
        .select('state_data, last_modified')
        .eq('user_id', user.id)
        .eq('route', currentRoute)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        throw error;
      }

      if (data?.state_data) {
        const stateData = data.state_data as any;
        
        // Convert plain object back to Map
        const fieldsMap = new Map<string, FieldState>();
        if (stateData.fields) {
          Object.entries(stateData.fields).forEach(([key, value]: [string, any]) => {
            fieldsMap.set(key, {
              value: value.value,
              timestamp: value.timestamp,
              metadata: value.metadata,
              isDirty: false, // Mark as clean since it's loaded from storage
            });
          });
        }

        dispatch({ type: 'LOAD_STATE', fields: fieldsMap });
        dispatch({ type: 'SET_LAST_SAVED', timestamp: new Date(data.last_modified) });
        console.log('Page state loaded successfully');
      }
    } catch (error) {
      console.error('Failed to load page state:', error);
      // Don't use toast here as it can cause hook call errors
    } finally {
      dispatch({ type: 'SET_LOADING', loading: false });
    }
  }, [isAuthenticated, user?.id, currentRoute, getToken]);

  const clearState = useCallback(() => {
    dispatch({ type: 'CLEAR_STATE' });
  }, []);

  // Auto-save every 30 seconds if there are unsaved changes
  useEffect(() => {
    if (!state.hasUnsavedChanges || state.isSaving) return;

    const autoSaveTimer = setTimeout(() => {
      saveState();
    }, 30000); // 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [state.hasUnsavedChanges, state.isSaving, saveState]);

  // Load state on route change
  useEffect(() => {
    loadState();
  }, [loadState, currentRoute]);

  // Save state before unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (state.hasUnsavedChanges && isAuthenticated) {
        saveState();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.hasUnsavedChanges, isAuthenticated, saveState]);

  const contextValue: PageStateContext = {
    trackedFields: state.trackedFields,
    updateField,
    getFieldValue,
    saveState,
    loadState,
    clearState,
    isLoading: state.isLoading,
    isSaving: state.isSaving,
    lastSaved: state.lastSaved,
    hasUnsavedChanges: state.hasUnsavedChanges,
  };

  return (
    <PageStateContext.Provider value={contextValue}>
      {children}
    </PageStateContext.Provider>
  );
};

export const usePageState = () => {
  const context = useContext(PageStateContext);
  if (context === undefined) {
    throw new Error('usePageState must be used within a PageStateProvider');
  }
  return context;
};
